/**
 * Discrete Step-Based Parallax Scroll System
 * Creates step-by-step scrolling behavior where each scroll gesture advances to the next section
 */

class ParallaxStepped {
  constructor(sectionElement) {
    this.section = sectionElement
    this.sectionId = sectionElement.dataset.sectionId
    this.contentBlocks = sectionElement.querySelectorAll('.content-block')
    this.rotatingImage = sectionElement.querySelector('.rotating-image')
    this.rotatingImageContainer = sectionElement.querySelector('.rotating-image-container')
    this.continueScrolling = sectionElement.querySelector('.continue-scrolling')

    this.currentIndex = 0
    this.totalBlocks = this.contentBlocks.length
    this.isTransitioning = false
    this.isInView = false
    this.touchStartY = null
    this.touchMoved = false

    // Discrete rotation values for each step
    this.rotationValues = [0, 0, 15, 0] // Block 1, 2, 3, 4

    // Discrete horizontal translation values (percentage) for each step
    this.translationValues = [0, 0, -10, 0] // Block 1, 2, 3, 4 (last one shifts right)

    // Discrete scale values for each step
    this.scaleValues = [0.9, 1, 1, 1] // Block 1, 2, 3, 4 (scale up on first transition)

    this.mobileScaleValues = [0.9, 1.4, 1.4, 1] // Block 1, 2, 3, 4 (Mobile scale up on first transition)

    this.translationValuesY = [0, 10, 10, 0]

    this.init()
  }

  init() {
    if (this.totalBlocks === 0) return

    this.setupInitialState()
    this.bindEvents()

    // Show first block immediately
    this.showBlock(0)
    this.updateRotatingImage()

    // Hide continue scrolling after a delay
    setTimeout(() => {
      if (this.continueScrolling) {
        this.continueScrolling.style.opacity = '1'
      }
    }, 1000)
  }

  setupInitialState() {
    // Hide all blocks initially and disable pointer events
    this.contentBlocks.forEach((block, index) => {
      block.classList.add('opacity-0', 'translate-y-8')
      block.classList.remove('opacity-100', 'translate-y-0')
      block.style.pointerEvents = 'none'
    })

    // Hide rotating image initially with combined transform (start at scale 0.8)
    if (this.rotatingImage) {
      this.rotatingImage.style.opacity = '0'
      this.rotatingImage.style.transform = 'translateX(0%) rotate(-15deg) scale(0.90)'
    }

    // Setup responsive layout
    this.updateLayout()
  }

  bindEvents() {
    // Use wheel event for better control
    this.section.addEventListener('wheel', this.handleWheel.bind(this), { passive: false })

    // Touch events for mobile (passive: false to allow preventDefault)
    this.section.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false })
    this.section.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false })
    this.section.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false })

    // Mouse events for desktop (to debug click issues)
    this.section.addEventListener('mousedown', this.handleMouseDown.bind(this), { passive: false })
    this.section.addEventListener('click', this.handleClick.bind(this), { passive: false })

    // Keyboard navigation
    window.addEventListener('keydown', this.handleKeydown.bind(this))

    // Resize handler
    window.addEventListener('resize', this.debounce(this.updateLayout.bind(this), 250))

    // Intersection Observer to handle when section is in view
    this.observeSection()
  }

  handleWheel(event) {
    // Only handle wheel events when section is in view
    if (!this.isInView || this.isTransitioning) {
      if (this.isTransitioning) {
        event.preventDefault()
      }
      return
    }

    const delta = event.deltaY
    const scrollingDown = delta > 0
    const scrollingUp = delta < 0

    // Check if we're at boundaries and trying to scroll out
    const atFirstStep = this.currentIndex === 0
    const atLastStep = this.currentIndex === this.totalBlocks - 1

    // Allow normal page scrolling only when trying to escape the section
    if ((atFirstStep && scrollingUp) || (atLastStep && scrollingDown)) {
      // Don't prevent default - allow page to scroll past section
      return
    }

    // Otherwise, capture the scroll for our discrete stepping
    event.preventDefault()

    // Use a more sensitive threshold and immediate transition lock
    const minDelta = Math.abs(delta)

    if (minDelta >= 10) {
      // Much lower threshold for more responsive detection
      // Immediately set transitioning to prevent rapid-fire
      this.isTransitioning = true

      if (scrollingDown && !atLastStep) {
        this.nextStep()
      } else if (scrollingUp && !atFirstStep) {
        this.previousStep()
      } else {
        // If we can't move, immediately release the lock
        this.isTransitioning = false
      }
    }
  }

  handleMouseDown(event) {
    const target = event.target
    if (this.isClickableElement(target)) {
      return // Don't interfere
    }
  }

  handleClick(event) {
    const target = event.target
    if (this.isClickableElement(target)) {
      return // Don't interfere
    }
  }

  handleTouchStart(event) {
    if (!this.isInView || this.isTransitioning) return

    // Check if touch target is a clickable element
    const target = event.target

    if (this.isClickableElement(target)) {
      // Don't interfere with button/link clicks
      this.touchStartY = null
      this.touchMoved = false
      return
    }

    this.touchStartY = event.touches[0].clientY
    this.touchMoved = false
  }

  handleTouchMove(event) {
    if (!this.isInView || this.isTransitioning || !this.touchStartY) {
      return
    }

    // Double-check that we're not on a clickable element
    const target = event.target
    if (this.isClickableElement(target)) {
      return
    }

    const currentY = event.touches[0].clientY
    const deltaY = this.touchStartY - currentY
    const swipingUp = deltaY > 0 // Scrolling down
    const swipingDown = deltaY < 0 // Scrolling up

    // Check boundaries - same logic as wheel events
    const atFirstStep = this.currentIndex === 0
    const atLastStep = this.currentIndex === this.totalBlocks - 1

    // Allow normal scrolling only when trying to escape the section
    if ((atFirstStep && swipingDown) || (atLastStep && swipingUp)) {
      // Don't prevent default - allow page to scroll past section
      return
    }

    // Otherwise, prevent scrolling to capture the gesture

    event.preventDefault()
    this.touchMoved = true
  }

  handleTouchEnd(event) {
    if (!this.isInView || !this.touchStartY || this.isTransitioning) {
      this.touchStartY = null
      this.touchMoved = false
      return
    }

    // Only process the gesture if we captured it (touchMoved is true)
    if (!this.touchMoved) {
      this.touchStartY = null
      this.touchMoved = false
      return
    }

    const touchEndY = event.changedTouches[0].clientY
    const deltaY = this.touchStartY - touchEndY
    const swipingUp = deltaY > 0 // Scrolling down
    const swipingDown = deltaY < 0 // Scrolling up

    if (Math.abs(deltaY) > 30) {
      // Check boundaries - same logic as wheel and touchmove
      const atFirstStep = this.currentIndex === 0
      const atLastStep = this.currentIndex === this.totalBlocks - 1

      // If we're at boundaries, we wouldn't have captured the gesture anyway
      // So we can proceed with stepping

      // Immediately set transitioning to prevent rapid-fire
      this.isTransitioning = true

      // Handle discrete stepping
      if (swipingUp && !atLastStep) {
        this.nextStep()
      } else if (swipingDown && !atFirstStep) {
        this.previousStep()
      } else {
        // If we can't move, immediately release the lock
        this.isTransitioning = false
      }
    }

    this.touchStartY = null
    this.touchMoved = false
  }

  handleKeydown(event) {
    if (this.isTransitioning) return

    // Set transitioning immediately for keyboard navigation too
    this.isTransitioning = true

    switch (event.key) {
      case 'ArrowDown':
      case 'PageDown':
      case ' ':
        event.preventDefault()
        if (this.currentIndex < this.totalBlocks - 1) {
          this.nextStep()
        } else {
          this.isTransitioning = false // Release lock if can't move
        }
        break
      case 'ArrowUp':
      case 'PageUp':
        event.preventDefault()
        if (this.currentIndex > 0) {
          this.previousStep()
        } else {
          this.isTransitioning = false // Release lock if can't move
        }
        break
      case 'Home':
        event.preventDefault()
        if (this.currentIndex !== 0) {
          this.goToStep(0)
        } else {
          this.isTransitioning = false // Release lock if already at home
        }
        break
      case 'End':
        event.preventDefault()
        if (this.currentIndex !== this.totalBlocks - 1) {
          this.goToStep(this.totalBlocks - 1)
        } else {
          this.isTransitioning = false // Release lock if already at end
        }
        break
      default:
        this.isTransitioning = false // Release lock for unhandled keys
    }
  }

  nextStep() {
    if (this.currentIndex < this.totalBlocks - 1) {
      this.goToStep(this.currentIndex + 1)
    }
  }

  previousStep() {
    if (this.currentIndex > 0) {
      this.goToStep(this.currentIndex - 1)
    }
  }

  goToStep(index) {
    if (index < 0 || index >= this.totalBlocks || index === this.currentIndex) {
      return
    }

    // If not already transitioning, set it now (for keyboard navigation)
    if (!this.isTransitioning) {
      this.isTransitioning = true
    }

    // Hide current block
    this.hideBlock(this.currentIndex)

    // Update index
    this.currentIndex = index

    // Show new block after a short delay
    setTimeout(() => {
      this.showBlock(this.currentIndex)
      this.updateRotatingImage()
      this.updateLayout()

      // Hide continue scrolling after first interaction
      if (this.continueScrolling && this.currentIndex > 0) {
        this.continueScrolling.style.opacity = '0'
      }

      setTimeout(() => {
        this.isTransitioning = false
      }, 500) // Slightly shorter to be more responsive
    }, 150) // Shorter delay for snappier response
  }

  showBlock(index) {
    const block = this.contentBlocks[index]
    if (!block) return

    block.classList.remove('opacity-0', 'translate-y-8')
    block.classList.add('opacity-100', 'translate-y-0')
    block.style.pointerEvents = 'auto' // Enable pointer events for active block
  }

  hideBlock(index) {
    const block = this.contentBlocks[index]
    if (!block) return

    block.classList.remove('opacity-100', 'translate-y-0')
    block.classList.add('opacity-0', 'translate-y-8')
    block.style.pointerEvents = 'none' // Disable pointer events for inactive block
  }

  updateRotatingImage() {
    if (!this.rotatingImage) return

    const shouldShowImage = this.currentIndex >= 1 // Show from block 2 onwards
    const rotation = this.rotationValues[this.currentIndex] || 0
    const translationX = this.translationValues[this.currentIndex] || 0
    const translateY = this.translationValuesY[this.currentIndex] || 0
    const isMobile = window.innerWidth < 750
    const scale = isMobile ? this.mobileScaleValues[this.currentIndex] || 1 : this.scaleValues[this.currentIndex] || 1

    if (shouldShowImage) {
      // Set opacity and combined transform
      // Skip horizontal translation on mobile
      this.rotatingImage.style.opacity = '1'
      if (isMobile) {
        // Mobile: only rotation and scale, no horizontal translation
        this.rotatingImage.style.transform = `rotate(${rotation}deg) scale(${scale}) translateY(${translateY}%)`
      } else {
        // Desktop: full transform with translation
        this.rotatingImage.style.transform = `translateX(${translationX}%) rotate(${rotation}deg) scale(${scale})`
      }
    } else {
      // When hiding image (going back to block 1), fade out with initial scale
      this.rotatingImage.style.opacity = '0'

      if (isMobile) {
        // Mobile: no horizontal translation
        this.rotatingImage.style.transform = 'rotate(-15deg) scale(0.90)'
      } else {
        // Desktop: include horizontal translation reset
        this.rotatingImage.style.transform = 'translateX(0%) rotate(-15deg) scale(0.90)'
      }
    }
  }

  updateLayout() {
    const isMobile = window.innerWidth < 750

    this.contentBlocks.forEach((block, index) => {
      const layoutContainer = block.querySelector('[data-block-index]')
      const textContent = block.querySelector('.block-text-content')

      if (!layoutContainer || !textContent) return

      if (isMobile) {
        this.updateMobileLayout(block, layoutContainer, textContent, index)
      } else {
        this.updateDesktopLayout(block, layoutContainer, textContent, index)
      }
    })
  }

  updateMobileLayout(block, layoutContainer, textContent, index) {
    // Reset classes
    block.classList.remove('mobile-with-image', 'mobile-with-image-centered', 'mobile-no-image')

    const hasImage = index >= 1 // Block's layout based on its own index, not current state
    if (index === 3) {
      block.classList.add('mobile-with-image-last')
    } else if (hasImage) {
      block.classList.add('mobile-with-image')
    } else {
      block.classList.add('mobile-no-image')
    }

    // Mobile layout classes
    layoutContainer.className = 'flex flex-col items-center gap-8 page-width w-full'
    textContent.className = 'w-full flex flex-col text-center block-text-content'
    textContent.style.marginLeft = '0'
    textContent.style.marginRight = '0'
  }

  updateDesktopLayout(block, layoutContainer, textContent, index) {
    const textPosition = block.getAttribute('data-text-position') || 'left'
    const hasImage = index >= 1 // Block's layout based on its own index, not current state

    // Clear mobile classes
    block.classList.remove('mobile-with-image', 'mobile-with-image-centered', 'mobile-no-image')

    if (hasImage && index >= 1 && index <= 3) {
      // Blocks 2-4: Image centered, text positioned
      if (textPosition === 'right') {
        layoutContainer.className = 'flex flex-row items-center justify-center gap-16 page-width w-full'
        textContent.className = 'w-full flex flex-col text-left block-text-content ml-auto'
        textContent.style.marginLeft = '60%'
        textContent.style.marginRight = '0'
      } else {
        layoutContainer.className = 'flex flex-row items-center justify-center gap-16 page-width w-full'
        textContent.className = 'w-full flex flex-col text-left block-text-content mr-auto'
        textContent.style.marginRight = '60%'
        textContent.style.marginLeft = '0'
      }
    } else if (hasImage && index === 4) {
      // Block 5: Side-by-side layout
      layoutContainer.className = 'flex flex-row items-center justify-center gap-16 page-width w-full'
      if (textPosition === 'right') {
        textContent.className = 'w-full flex flex-col text-left block-text-content order-2'
      } else {
        textContent.className = 'w-full flex flex-col text-left block-text-content order-1'
      }
      textContent.style.marginLeft = '0'
      textContent.style.marginRight = '0'
    } else {
      // Block 1 or no image: Centered layout
      layoutContainer.className = 'flex flex-col items-center gap-8 page-width w-full'
      textContent.className = 'w-full flex flex-col max-w-5xl text-center block-text-content'
      textContent.style.marginLeft = '0'
      textContent.style.marginRight = '0'
    }
  }

  observeSection() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            // Section is sufficiently in view - enable scroll capture
            this.isInView = true
            this.section.classList.remove('parallax-scroll-disabled')
          } else {
            // Section is out of view - disable scroll capture
            this.isInView = false
            this.section.classList.add('parallax-scroll-disabled')
          }
        })
      },
      {
        threshold: [0, 0.1, 0.3, 0.5, 0.7, 0.9, 1],
        rootMargin: '-10px 0px -10px 0px', // Slight margin to be more precise
      }
    )

    observer.observe(this.section)
  }

  // Check if an element is clickable and should not be interfered with
  isClickableElement(element) {
    if (!element) return false

    // Check the element itself and its parents up the tree
    let current = element
    while (current && current !== this.section) {
      const tagName = current.tagName.toLowerCase()

      // Common clickable elements
      if (['a', 'button', 'input', 'select', 'textarea'].includes(tagName)) {
        return true
      }

      // Elements with click handlers or interactive roles
      if (
        current.onclick ||
        current.getAttribute('role') === 'button' ||
        current.hasAttribute('data-clickable') ||
        current.classList.contains('clickable')
      ) {
        return true
      }

      current = current.parentElement
    }

    return false
  }

  // Utility function for debouncing
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
}

// Initialize all parallax sections
document.addEventListener('DOMContentLoaded', function () {
  const parallaxSections = document.querySelectorAll('[data-section-id]')

  parallaxSections.forEach((section) => {
    // Only initialize if it has the video-sticky-section class
    if (section.classList.contains('video-sticky-section')) {
      new ParallaxStepped(section)
    }
  })
})

// Handle reduced motion preference
if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  document.documentElement.style.setProperty('--transition-duration', '0s')
} else {
  document.documentElement.style.setProperty('--transition-duration', '1s')
}
